import React, { useState } from "react";
import { signInWithEmail, signInWithGoogle, signInWithGitHub } from "../../lib/auth";
import "./auth-styles.css";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

export default function SignInPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Client-side quick checks
    if (!email || !password) {
      setError("Please fill in all fields.");
      setLoading(false);
      return;
    }

    try {
      await signInWithEmail(email, password);
      // Success: redirect to dashboard
      window.location.href = "/coming-soon";
    } catch (err: any) {
      console.error("Sign in failed:", err);
      // Prevent user enumeration leaks by using standard generic error
      setError("Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  const handleOAuth = async (provider: "google" | "github") => {
    setLoading(true);
    setError(null);
    try {
      if (provider === "google") {
        await signInWithGoogle();
      } else {
        await signInWithGitHub();
      }
    } catch (err: any) {
      console.error(`${provider} auth failed:`, err);
      setError(`Failed to sign in with ${provider}.`);
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {/* Background blobs */}
      <div className="ambient-bg">
        <div className="blob blob-1"></div>
        <div className="blob blob-2"></div>
      </div>

      <div className="auth-card">
        <div className="auth-header">
          <h1 className="auth-logo">
            Huddlezen
            <span className="logo-dot"></span>
          </h1>
          <p className="auth-subtitle">Sign in to your spatial office</p>
        </div>

        {error && <div className="error-banner">{error}</div>}

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="email">
              Email Address
            </label>
            <input
              className="form-input"
              id="email"
              type="email"
              placeholder="name@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="password">
              Password
            </label>
            <input
              className="form-input"
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              required
            />
          </div>

          <button
            className="auth-btn auth-btn-primary"
            type="submit"
            disabled={loading}
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <div className="auth-divider">or continue with</div>

        <div className="oauth-group">
          <button
            className="auth-btn oauth-btn"
            onClick={() => handleOAuth("google")}
            disabled={loading}
          >
            <svg
              className="oauth-icon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                fill="#EA4335"
              />
            </svg>
            Google
          </button>

          <button
            className="auth-btn oauth-btn"
            onClick={() => handleOAuth("github")}
            disabled={loading}
          >
            <svg
              className="oauth-icon"
              viewBox="0 0 24 24"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.831.092-.646.35-1.086.636-1.336-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.579.688.481C19.137 20.162 22 16.418 22 12c0-5.523-4.477-10-10-10z"
              />
            </svg>
            GitHub
          </button>
        </div>

        {import.meta.env.DEV && (
          <div style={{ marginTop: "1rem" }}>
            <button
              className="auth-btn"
              style={{
                backgroundColor: "#efeae2",
                color: "#5a6e58",
                border: "1px dashed #5a6e58",
                width: "100%",
                padding: "0.6rem",
                borderRadius: "8px",
                fontWeight: "bold",
                cursor: "pointer"
              }}
              type="button"
              onClick={async () => {
                if (!email || !password) {
                  setError("Please fill in email and password above to bypass signup.");
                  return;
                }
                setLoading(true);
                setError(null);
                try {
                  const res = await fetch(`${API_URL}/v1/dev/signup-bypass`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ email, password }),
                  });
                  if (!res.ok) {
                    const errData = await res.json();
                    throw new Error(errData.detail || "Signup bypass failed");
                  }
                  // Standard Sign In once confirmed
                  await signInWithEmail(email, password);
                  window.location.href = "/coming-soon";
                } catch (err: any) {
                  console.error("Developer login bypass failed:", err);
                  setError(err.message || "Bypass failed.");
                } finally {
                  setLoading(false);
                }
              }}
            >
              🧪 Dev Mode: Register & Sign In Bypass
            </button>
          </div>
        )}

        <div className="auth-footer">
          Don't have an account?
          <a className="auth-link" href="/signup">
            Sign Up
          </a>
        </div>
      </div>
    </div>
  );
}
